import type { Collection } from '@prisma/client'
import type { SerializeFrom } from '@remix-run/server-runtime'

import type { AmendableAttachmentType } from '~/contexts/MediaManagerContext'
import type { CollectionStatus } from '~/schemas/collection'
import { isNonNullString } from '~/utils/string'

import type {
  CollectionAttachments,
  CollectionFormData,
  CollectionItemFormData,
  SerializedCollectionItemWithProduct,
} from './type'

export const defaultCollectionFormData: CollectionFormData = {
  name: '',
  description: '',
  mediaFormat: 'square',
  slideshowMediaFormat: null,
  avatarSrc: null,
}

export const updateCollectionFormData = (
  data: CollectionFormData | SerializeFrom<Collection>
): CollectionFormData => {
  const removeEmptyFields = (obj: Record<string, unknown>) => {
    return Object.fromEntries(
      Object.entries(obj).filter(([key, value]) => {
        // Preserve publishedAt and archivedAt even when null for status calculation
        if (key === 'publishedAt' || key === 'archivedAt') {
          return true
        }
        return value !== '' && value !== undefined && value !== null
      })
    )
  }

  // Handle special case where publishedAt/archivedAt might be undefined from serialization
  const processedData = { ...data }
  if (
    'publishedAt' in processedData &&
    processedData.publishedAt === undefined
  ) {
    processedData.publishedAt = null
  }
  if ('archivedAt' in processedData && processedData.archivedAt === undefined) {
    processedData.archivedAt = null
  }

  return {
    ...structuredClone(defaultCollectionFormData),
    ...structuredClone(removeEmptyFields(processedData)),
  }
}

export const updateCollectionItemsFormData = (
  data?: CollectionItemFormData[] | SerializedCollectionItemWithProduct[]
): CollectionItemFormData[] => {
  if (!Array.isArray(data) || data.length === 0) {
    return []
  }

  return data.map((item) => ({
    id: item.id,
    collectionId: item.collectionId || '',
    productId: item.productId,
    disposition: item.disposition || '0',
    product: item.product,
  }))
}

export const updateCollectionAttachmentsFormData = (
  data?: CollectionAttachments
): AmendableAttachmentType[] => {
  if (!Array.isArray(data) || data.length === 0) {
    return []
  }

  return data as unknown as AmendableAttachmentType[]
}

export const calculateCollectionStatus = (
  collection: CollectionFormData
): 'draft' | 'live' | 'archived' | 'unsaved' => {
  if (!collection.id) {
    return 'unsaved'
  }

  if (collection.archivedAt) {
    return 'archived'
  }

  if (!collection.publishedAt) {
    return 'draft'
  }

  return 'live'
}

export const collectionItemHelpers = {
  // Add a single item
  addItem: (
    currentItems: CollectionItemFormData[],
    newItem: CollectionItemFormData
  ) => {
    return [...currentItems, newItem]
  },

  // Remove an item by product ID
  removeItem: (currentItems: CollectionItemFormData[], productId: string) => {
    return currentItems.filter((item) => item.productId !== productId)
  },

  // Reorder items
  reorderItems: (
    currentItems: CollectionItemFormData[],
    newOrder: { id: string; disposition: number }[]
  ) => {
    return currentItems.map((item) => {
      const reorderItem = newOrder.find((r) => r.id === item.id)
      return reorderItem
        ? { ...item, disposition: reorderItem.disposition }
        : item
    })
  },

  // Update a single item
  updateItem: (
    currentItems: CollectionItemFormData[],
    itemId: string,
    updates: Partial<CollectionItemFormData>
  ) => {
    return currentItems.map((item) =>
      item.id === itemId ? { ...item, ...updates } : item
    )
  },
}

export const collectionAttachmentHelpers = {
  // Add a single attachment
  addAttachment: (
    currentAttachments: CollectionAttachments,
    newAttachment: CollectionAttachments[0]
  ) => {
    return [...currentAttachments, newAttachment]
  },

  // Remove an attachment by ID
  removeAttachment: (
    currentAttachments: CollectionAttachments,
    attachmentId: string
  ) => {
    return currentAttachments.filter(
      (attachment) => attachment.id !== attachmentId
    )
  },

  // Reorder attachments
  reorderAttachments: (
    currentAttachments: CollectionAttachments,
    newOrder: { id: string; disposition: number }[]
  ) => {
    return currentAttachments.map((attachment) => {
      const reorderItem = newOrder.find((r) => r.id === attachment.id)
      return reorderItem
        ? { ...attachment, disposition: reorderItem.disposition }
        : attachment
    })
  },

  // Update a single attachment
  updateAttachment: (
    currentAttachments: CollectionAttachments,
    attachmentId: string,
    updates: Partial<CollectionAttachments[0]>
  ) => {
    return currentAttachments.map((attachment) =>
      attachment.id === attachmentId
        ? { ...attachment, ...updates }
        : attachment
    )
  },
}

export type CollectionConfigurationError = {
  code:
    | 'collection_name_required'
    | 'collection_name_is_too_short'
    | 'collection_description_is_too_short'
    | 'collection_thumbnail_required'
    | 'collection_products_required'
    | 'collection_products_not_published'
    | 'collection_products_archived'
}

// Field Registration Utilities
export type CollectionFieldId = keyof CollectionFormData | 'collectionItems'
export const getCollectionFieldId = (
  collection: CollectionFormData,
  name: CollectionFieldId
) => {
  return `${collection.id || 'new-collection'}-${name}`
}

export const registerCollectionField = (
  collection: CollectionFormData,
  name: CollectionFieldId,
  errors: Map<string, CollectionConfigurationError>
) => {
  const hasError = errors.has(getCollectionFieldId(collection, name))

  return {
    id: getCollectionFieldId(collection, name),
    invalid: hasError ? true : undefined,
    'aria-invalid': hasError,
  }
}

type PublishCollectionConfiguration = {
  collection: CollectionFormData
  collectionItems?: CollectionItemFormData[]
  status?: CollectionStatus | 'unsaved'
}

export const validateCollectionConfiguration = ({
  collection,
}: PublishCollectionConfiguration) => {
  const errors: Map<string, CollectionConfigurationError> = new Map()

  if (!isNonNullString(collection.name)) {
    errors.set(getCollectionFieldId(collection, 'name'), {
      code: 'collection_name_required',
    })
  } else if (collection.name.length < 3) {
    errors.set(getCollectionFieldId(collection, 'name'), {
      code: 'collection_name_is_too_short',
    })
  }

  if (
    isNonNullString(collection.description) &&
    collection.description.length < 5
  ) {
    errors.set(getCollectionFieldId(collection, 'description'), {
      code: 'collection_description_is_too_short',
    })
  }

  return errors
}

export const validateCollectionForPublishing = ({
  collection,
  collectionItems,
}: PublishCollectionConfiguration) => {
  // Basic validation first
  const errors = validateCollectionConfiguration({
    collection,
    status: 'draft',
    collectionItems,
  })

  // Require thumbnail image
  if (!isNonNullString(collection.avatarSrc)) {
    errors.set(getCollectionFieldId(collection, 'avatarSrc'), {
      code: 'collection_thumbnail_required',
    })
  }

  // Require products
  if (!collectionItems || collectionItems.length === 0) {
    errors.set(getCollectionFieldId(collection, 'collectionItems'), {
      code: 'collection_products_required',
    })
  } else {
    // Check that all products are not archived
    const archivedProducts = collectionItems.filter(
      (item) => item.product.archivedAt !== null
    )

    if (archivedProducts.length > 0) {
      errors.set(getCollectionFieldId(collection, 'collectionItems'), {
        code: 'collection_products_archived',
      })
    }

    // Check that all products are published
    const draftProducts = collectionItems.filter(
      (item) => !item.product.publishedAt
    )

    if (draftProducts.length > 0) {
      errors.set(getCollectionFieldId(collection, 'collectionItems'), {
        code: 'collection_products_not_published',
      })
    }
  }

  return errors
}

// Deep equality utility functions
export const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true

  if (a == null || b == null) return a === b

  if (typeof a !== typeof b) return false

  if (typeof a !== 'object') return a === b

  if (Array.isArray(a) !== Array.isArray(b)) return false

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((item, index) => deepEqual(item, b[index]))
  }

  const keysA = Object.keys(a as Record<string, unknown>)
  const keysB = Object.keys(b as Record<string, unknown>)

  if (keysA.length !== keysB.length) return false

  return keysA.every(
    (key) =>
      keysB.includes(key) &&
      deepEqual(
        (a as Record<string, unknown>)[key],
        (b as Record<string, unknown>)[key]
      )
  )
}

export const areCollectionConfigurationsEqual = (
  configA: {
    collection: CollectionFormData
    collectionItems: CollectionItemFormData[]
    collectionAttachments: AmendableAttachmentType[]
  },
  configB: {
    collection: CollectionFormData
    collectionItems: CollectionItemFormData[]
    collectionAttachments: AmendableAttachmentType[]
  }
): boolean => {
  // Exclude publishedAt and archivedAt from comparison since these are managed by separate publish/unpublish and archive/unarchive actions
  const {
    publishedAt: publishedAtA,
    archivedAt: archivedAtA,
    ...collectionA
  } = configA.collection
  const {
    publishedAt: publishedAtB,
    archivedAt: archivedAtB,
    ...collectionB
  } = configB.collection

  return (
    deepEqual(collectionA, collectionB) &&
    deepEqual(configA.collectionItems, configB.collectionItems) &&
    deepEqual(configA.collectionAttachments, configB.collectionAttachments)
  )
}
